export type ZoomLevel = 'fit' | number;

export interface Size {
  width: number;
  height: number;
}

export interface Point {
  x: number;
  y: number;
}

export const ZOOM_STEPS: readonly number[] = [1, 2, 4];

export const STAGE_INSET = 16;

export const ORIGIN: Point = { x: 0, y: 0 };

const MAX_ZOOM = Math.max(...ZOOM_STEPS);

export function fitScale(natural: Size, stage: Size): number {
  const width = stage.width - STAGE_INSET * 2;
  const height = stage.height - STAGE_INSET * 2;
  if (natural.width <= 0 || natural.height <= 0 || width <= 0 || height <= 0) return 1;
  return Math.min(1, width / natural.width, height / natural.height);
}

export function zoomScale(level: ZoomLevel, fit: number): number {
  return level === 'fit' ? fit : level;
}

export function stepZoom(level: ZoomLevel, fit: number, direction: 1 | -1): ZoomLevel {
  const steps = ZOOM_STEPS.filter((step) => step > fit);
  const scale = zoomScale(level, fit);
  if (direction > 0) return steps.find((step) => step > scale) ?? level;
  if (level === 'fit') return level;
  return steps.filter((step) => step < scale).at(-1) ?? 'fit';
}

export function canZoomIn(level: ZoomLevel): boolean {
  return level === 'fit' || level < MAX_ZOOM;
}

export function zoomAround(pan: Point, anchor: Point, fromScale: number, toScale: number): Point {
  const ratio = fromScale > 0 ? toScale / fromScale : 1;
  return {
    x: anchor.x - (anchor.x - pan.x) * ratio,
    y: anchor.y - (anchor.y - pan.y) * ratio,
  };
}

function clamp(value: number, limit: number): number {
  const clamped = Math.min(limit, Math.max(-limit, value));
  return clamped === 0 ? 0 : clamped;
}

export function clampPan(pan: Point, natural: Size, scale: number, stage: Size): Point {
  const limitX = Math.max(0, (natural.width * scale - (stage.width - STAGE_INSET * 2)) / 2);
  const limitY = Math.max(0, (natural.height * scale - (stage.height - STAGE_INSET * 2)) / 2);
  return { x: clamp(pan.x, limitX), y: clamp(pan.y, limitY) };
}
